import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { initAnalytics } from '@/utils/analytics';

const CONSENT_KEY = 'conexus_cookie_consent';

type ConsentState = 'pending' | 'accepted' | 'rejected';

function getStoredConsent(): ConsentState {
  const stored = localStorage.getItem(CONSENT_KEY);
  if (stored === 'accepted' || stored === 'rejected') return stored;
  return 'pending';
}

const CookieConsent = () => {
  const { t } = useTranslation();
  const [consent, setConsent] = useState<ConsentState>('accepted'); // avoid flash
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = getStoredConsent();
    setConsent(stored);
    if (stored === 'pending') {
      // Show banner after a short delay
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
    if (stored === 'accepted') {
      initAnalytics();
    }
  }, []);

  const accept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    setConsent('accepted');
    setVisible(false);
    initAnalytics();
  };

  const reject = () => {
    localStorage.setItem(CONSENT_KEY, 'rejected');
    setConsent('rejected');
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && consent === 'pending' && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25 }}
          className="fixed bottom-0 left-0 right-0 z-[60] p-4 md:p-6"
        >
          <div className="max-w-4xl mx-auto bg-card border border-border rounded-2xl shadow-xl p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
            <Cookie className="h-8 w-8 text-primary flex-shrink-0 mt-1 md:mt-0" />
            <div className="flex-1">
              <p className="text-sm text-foreground font-medium mb-1">
                {t('cookies.title', 'We use cookies')}
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t(
                  'cookies.description',
                  'We use cookies and tracking technologies to improve your experience, analyze site traffic, and support our marketing efforts. You can accept all cookies or reject non-essential ones.'
                )}
              </p>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <Button variant="outline" size="sm" onClick={reject}>
                {t('cookies.reject', 'Reject')}
              </Button>
              <Button size="sm" onClick={accept}>
                {t('cookies.accept', 'Accept All')}
              </Button>
            </div>
            <button
              onClick={reject}
              className="absolute top-3 right-3 md:hidden text-muted-foreground hover:text-foreground"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
